import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, MessageCircle, BookOpen, Users, Gift } from 'lucide-react';
import { VIDEOS } from '../lib/media';
import { useAuth } from '../components/auth';
import HeroVideo from '../components/HeroVideo';
import { billing } from '../lib/api';

export default function Success() {
  const { isAuthenticated, hasPurchased, isLoading } = useAuth();

  // Mark that this user came through the purchase flow
  useEffect(() => {
    sessionStorage.setItem('fc_just_purchased', '1');
    // Stripe sends the buyer back with the Checkout Session id. The Worker reads
    // that session from Stripe and grants access only if it is paid.
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    if (sessionId) billing.confirm(sessionId).catch(() => { /* sign-in checks again */ });
  }, []);

  // Show loading while auth is resolving
  if (isLoading) {
    return null;
  }

  // Not authenticated — show payment confirmation with sign-in instructions
  // This is the normal state for a first-time buyer arriving from checkout
  if (!isAuthenticated) {
    return (
      <>
        <section className="page-hero page-hero--full">
          <HeroVideo className="page-hero-video" src={VIDEOS.meatHero} />
          <div className="page-hero-overlay" />
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="success-hero-inner"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="success-check-circle"
              >
                <Check strokeWidth={3} size={48} color="white" />
              </motion.div>

              <h1 className="success-hero-title">
                Payment Successful
              </h1>
              <p className="success-hero-subtitle">
                You now have lifetime access to everything — books, guides, community, and all bonus materials.
              </p>

              <div style={{
                background: 'rgba(255,255,255,0.12)',
                backdropFilter: 'blur(8px)',
                borderRadius: '12px',
                padding: '32px',
                maxWidth: '440px',
                margin: '32px auto 0',
                textAlign: 'center',
              }}>
                <h3 style={{
                  color: '#fff',
                  fontSize: '18px',
                  fontWeight: 500,
                  margin: '0 0 12px',
                }}>
                  Next Step: Sign In to Access Your Library
                </h3>
                <p style={{
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: '15px',
                  lineHeight: 1.6,
                  margin: '0 0 24px',
                }}>
                  Click the button below to sign in. We will send you a secure link to your email — no password needed.
                </p>
                <Link
                  to="/login"
                  className="btn btn-primary btn-lg"
                  style={{ display: 'inline-block' }}
                >
                  Sign In to Your Account
                </Link>
              </div>

              <p style={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: '13px',
                marginTop: '20px',
              }}>
                A confirmation email with your receipt is on its way.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="section">
          <div className="container">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-section text-center mb-2xl">
                What's Waiting For You
              </h2>
              <div className="grid-3 mx-auto-wide gap-lg">
                {[
                  {
                    icon: <BookOpen size={32} />,
                    title: 'Your Library',
                    description: 'Four books and workbooks — read online anytime, anywhere. Start with Chapter 1.',
                  },
                  {
                    icon: <Users size={32} />,
                    title: 'The Community',
                    description: 'Thousands of members who get it. Support, questions answered, wins celebrated — 24/7.',
                  },
                  {
                    icon: <Gift size={32} />,
                    title: 'All Bonuses',
                    description: 'Quick start guides, trackers, food lists, protocols — everything you need to succeed.',
                  },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="card success-benefit-card"
                  >
                    <div className="success-benefit-icon">{item.icon}</div>
                    <h3 className="success-benefit-title">{item.title}</h3>
                    <p className="success-benefit-desc">{item.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        <section className="section section-light">
          <div className="container">
            <div className="content-narrow text-center">
              <h2 className="heading-section">Questions?</h2>
              <p className="success-support-text" style={{ marginTop: '16px' }}>
                Email us at{' '}
                <a href="mailto:support@fastingcarnivore.com">support@fastingcarnivore.com</a>
              </p>
            </div>
          </div>
        </section>
      </>
    );
  }

  // Authenticated but hasn't purchased — show them the join page
  if (!hasPurchased) {
    return (
      <section className="section">
        <div className="container">
          <div className="content-narrow text-center">
            <h2 className="heading-section">Almost There</h2>
            <p style={{ fontSize: '16px', color: '#555', lineHeight: 1.7, margin: '16px 0 32px' }}>
              Your account is set up, but we haven't received your payment yet.
              If you just completed checkout, it may take a moment to process.
            </p>
            <Link to="/join" className="btn btn-primary btn-lg" style={{ marginRight: '12px' }}>
              Get started
            </Link>
            <button
              onClick={() => window.location.reload()}
              className="btn btn-secondary btn-lg"
            >
              Refresh Status
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      {/* Hero */}
      <section className="page-hero page-hero--full">
        <HeroVideo className="page-hero-video" src={VIDEOS.meatHero} />
        <div className="page-hero-overlay" />
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="success-hero-inner"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="success-check-circle"
            >
              <Check strokeWidth={3} size={48} color="white" />
            </motion.div>

            <h1 className="success-hero-title">
              Welcome to the Tribe!
            </h1>
            <p className="success-hero-subtitle">
              You're in. Your payment was successful and you now have lifetime access to everything.
            </p>

            <motion.a
              href="https://discord.com/channels/1460874491822608557/1460874492652945461"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="success-discord-btn"
            >
              <MessageCircle size={24} />
              Join Discord Now
            </motion.a>

            <p className="success-hero-note">
              Everything you need is inside Discord—books, bonuses, and your community.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What You Get */}
      <section className="section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-section text-center mb-2xl">
              What's Waiting For You
            </h2>

            <div className="grid-3 mx-auto-wide gap-lg">
              {[
                {
                  icon: <Users size={32} />,
                  title: 'The Community',
                  description: 'Thousands of members who get it. Support, questions answered, wins celebrated—24/7.'
                },
                {
                  icon: <BookOpen size={32} />,
                  title: 'Both Books',
                  description: 'Fasting Carnivore and Nano Fasting—read online anytime, anywhere.'
                },
                {
                  icon: <Gift size={32} />,
                  title: 'All Bonuses',
                  description: 'Quick start guides, trackers, food lists, protocols—everything you need.'
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card success-benefit-card"
                >
                  <div className="success-benefit-icon">
                    {item.icon}
                  </div>
                  <h3 className="success-benefit-title">
                    {item.title}
                  </h3>
                  <p className="success-benefit-desc">
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Next Steps */}
      <section className="section section-light">
        <div className="container">
          <div className="content-narrow text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-section">Your Next Step</h2>
              <p className="success-next-text">
                Click the button below to join Discord. Once inside, you'll find links to everything
                in the welcome channel. We can't wait to meet you.
              </p>

              <Link
                to="/login"
                className="btn btn-primary btn-lg"
                style={{ marginBottom: '1rem', display: 'inline-block' }}
              >
                Sign In to Read Your Books
              </Link>

              <br />

              <a
                href="https://discord.com/channels/1460874491822608557/1460874492652945461"
                target="_blank"
                rel="noopener noreferrer"
                className="success-discord-btn-light"
              >
                <MessageCircle size={24} />
                Join the Community on Discord
              </a>

              <p className="success-support-text">
                Questions? Email us at{' '}
                <a href="mailto:support@fastingcarnivore.com">
                  support@fastingcarnivore.com
                </a>
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="success-footer">
        <div className="container">
          <Link to="/" className="success-footer-logo">
            Fasting Carnivore
          </Link>
          <p className="success-footer-copyright">
            &copy; {new Date().getFullYear()} Fasting Carnivore. All rights reserved.
          </p>
        </div>
      </footer>
    </>
  );
}
